import { useCallback, type MouseEvent } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextLink from 'LuxKit/TextLink'
import Caption from 'LuxKit/Typography/Caption'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'

export interface OfferBreadcrumbTrailItem {
  text: string
  url?: string
  actionable?: boolean
}

const Trail = styled.ul`
  list-style-type: none;
  margin: 0;
  min-width: 0;
  overflow: hidden;
  align-items: center;
  width: 100%;
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
  }

  > * + * {
    margin-inline-start: ${rem(4)};
  }

  > li {
    display: inline-flex;
    align-items: center;
    min-width: 0;

    > * + * {
      margin-inline-start: ${rem(4)};
    }

    > svg {
      flex-shrink: 0;
    }
  }
`

const Label = styled(Caption)`
  max-width: 16ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Link = styled(TextLink)`
  display: block;
  max-width: 16ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

interface Props {
  items: Array<OfferBreadcrumbTrailItem>
  onSelect?: (index: number) => void
}

/** Shared presentation; ancestors navigate, while the current offer is plain text. */
function OfferBreadcrumbTrail({ items, onSelect }: Props) {
  const handleSelect = useCallback((event: MouseEvent<HTMLElement>) => {
    onSelect?.(Number(event.currentTarget.dataset.crumbIndex))
  }, [onSelect])

  if (!items.length) return null

  return (
    <Trail>
      {items.map((item, index) => {
        const isCurrent = index === items.length - 1
        return (
          <li key={`${item.text}-${index}`}>
            {index > 0 && <LineAngleRightIcon size="XS" />}
            {!isCurrent && (item.url || item.actionable) ? (
              <Link
                to={item.url}
                data-crumb-index={index}
                onClick={item.actionable ? handleSelect : undefined}
                weight="regular"
                size="caption"
              >
                {item.text}
              </Link>
            ) : (
              <Label variant="medium" aria-current={isCurrent ? 'page' : undefined}>
                {item.text}
              </Label>
            )}
          </li>
        )
      })}
    </Trail>
  )
}

export default OfferBreadcrumbTrail
